import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';

// Upload gift card verification image
export const uploadGiftCardImage = async (userId, cardId, file) => {
  try {
    // Create unique filename
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storageRef = ref(storage, `giftcards/${userId}/${cardId}/${fileName}`);
    
    // Upload file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return {
      url: downloadURL,
      fileName: fileName,
      path: snapshot.ref.fullPath
    };
  } catch (error) {
    throw error;
  }
};

// Upload multiple gift card images
export const uploadMultipleGiftCardImages = async (userId, cardId, files) => {
  try {
    const uploadPromises = files.map(file => 
      uploadGiftCardImage(userId, cardId, file)
    );
    
    const results = await Promise.all(uploadPromises);
    return results;
  } catch (error) {
    throw error;
  }
};

// Delete gift card image
export const deleteGiftCardImage = async (imagePath) => {
  try {
    const imageRef = ref(storage, imagePath);
    await deleteObject(imageRef);
    return true;
  } catch (error) {
    throw error;
  }
};

// Get brand logo URL (public images)
export const getBrandLogoURL = async (brandName) => {
  try {
    const logoRef = ref(storage, `brands/${brandName.toLowerCase()}.png`);
    const url = await getDownloadURL(logoRef);
    return url;
  } catch (error) {
    // Return fallback or placeholder
    return '/images/brand-placeholder.png';
  }
};

// Upload brand logo (admin only)
export const uploadBrandLogo = async (brandName, file) => {
  try {
    const fileName = `${brandName.toLowerCase()}.png`;
    const storageRef = ref(storage, `brands/${fileName}`);
    
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    throw error;
  }
};

// Compress image before upload (optional utility)
export const compressImage = (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions
      const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
      canvas.width = img.width * ratio;
      canvas.height = img.height * ratio;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob(resolve, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

// Validate image file
export const validateImageFile = (file) => {
  const errors = [];
  
  // Check file type
  if (!file.type.startsWith('image/')) {
    errors.push('File must be an image');
  }
  
  // Check file size (5MB max)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    errors.push('Image must be smaller than 5MB');
  }
  
  // Check dimensions (optional)
  return new Promise((resolve) => {
    if (errors.length > 0) {
      resolve({ valid: false, errors });
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      // Check minimum dimensions
      if (img.width < 100 || img.height < 100) {
        errors.push('Image must be at least 100x100 pixels');
      }
      
      resolve({
        valid: errors.length === 0,
        errors,
        dimensions: { width: img.width, height: img.height }
      });
    };
    
    img.src = URL.createObjectURL(file);
  });
};